import { inputRichTextCommands } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';

import { presenceActions } from 'components/presence/actions/presenceActions';
import { actions as personalActions } from 'components/settings/actions/personal';
import {
  createCommand,
  createCommands,
  useCommandHandler,
} from 'utils/commands';
import { camelToSentenceCase } from 'utils/formatting/string';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import { appActions } from './app.actions';
import { useColorMode } from './utils/colorMode';

const { presenceSetStatus } = presenceActions;

export const globalCommands = createCommands([
  {
    group: 'app',
    label: 'Keyboard shortcuts cheat sheet',
    name: 'keyboardCheatSheet',
    description: 'Toggles the keyboard shortcuts cheat sheet',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl'], 'Slash'] }],
      isNonRebindable: true,
    },
    useRegisterHandler: () => {
      const dispatch = useAppDispatch();

      useCommandHandler({
        name: 'app:keyboardCheatSheet',
        actionFn: () => {
          dispatch(
            appActions.showModal({ modal: { type: 'app:keyboardCheatSheet' } }),
          );
        },
      });
    },
  },
  {
    group: 'app',
    label: 'Open dialer',
    name: 'openDialer',
    description: 'Opens the phone dialer',
    keyboard: { keybindings: [{ binding: [['$metaOrCtrl', 'Alt'], 'KeyP'] }] },
  },
  {
    group: 'app',
    label: 'Toggle presence',
    name: 'togglePresence',
    description: 'Toggles presence between working and away',
    keyboard: {
      keybindings_mac: [{ binding: [['Meta', 'Control', 'Shift'], 'Tab'] }],
      keybindings_pc: [{ binding: [['Control', 'Alt'], 'Backquote'] }],
    },
    useRegisterHandler: () => {
      const dispatch = useAppDispatch();
      const presence = useAppSelector((state) => state.presence.presence);

      useCommandHandler({
        name: 'app:togglePresence',
        actionFn: () => {
          const newStatus = presence.status === 'working' ? 'away' : 'working';

          dispatch(presenceSetStatus({ status: newStatus }));
        },
      });
    },
  },
  {
    group: 'app',
    label: 'Toggle light/dark mode',
    name: 'toggleColorMode',
    description: 'Toggles between light and dark mode',
    keyboard: {
      keybindings: [{ binding: [['Shift', '$controlOrAlt'], 'KeyA'] }],
    },
    featureFlag: 'dark-mode',
    useRegisterHandler: () => {
      const { toggleColorMode } = useColorMode();

      useCommandHandler({
        name: 'app:toggleColorMode',
        actionFn: () => {
          const colorMode = toggleColorMode();

          ampli.colorModeUpdated({
            colorMode,
            source: 'keyboardShortcut',
          });
        },
      });
    },
  },
  {
    group: 'app',
    label: 'Command Launcher',
    name: 'commandLauncher',
    description: 'Opens the Command Launcher',
    featureFlag: 'cmd-k',
    keyboard: {
      keybindings: [{ binding: [['$metaOrCtrl'], 'KeyK'] }],
      isNonRebindable: true,
    },
    useRegisterHandler: () => {
      const dispatch = useAppDispatch();

      useCommandHandler({
        name: 'app:commandLauncher',
        actionFn: () => {
          dispatch(
            appActions.showModal({ modal: { type: 'app:commandLauncher' } }),
          );
        },
      });
    },
  },
  {
    group: 'app',
    label: 'Toggle left sidebar',
    name: 'toggleLeftSidebar',
    description: 'Shows or hides the left navigation menu',
    keyboard: {
      keybindings: [{ binding: [[], 'BracketLeft'] }],
    },
    useRegisterHandler: () => {
      const dispatch = useAppDispatch();
      const isLeftMenuOpen = useAppSelector(
        (state) => state.settings.personal.isLeftMenuOpen,
      );

      useCommandHandler({
        name: 'app:toggleLeftSidebar',
        actionFn: () => {
          dispatch(
            personalActions.personalLeftMenu({
              key: 'isLeftMenuOpen',
              value: !isLeftMenuOpen,
            }),
          );
        },
      });
    },
  },
]);

/**
 * Represents the rich text editor commands.
 *
 * @remarks
 *   These commands are considered external and should not be handled internally
 *   by the RichTextEditor.
 */
export const richTextEditorCommands = [
  ...createCommands([
    {
      group: 'richTextEditor',
      name: 'send',
      label: 'Send message',
      isExternal: true,
      keyboard: {
        keybindings: [
          {
            binding: [['$metaOrCtrl'], 'Enter'],
          },
        ],
        isNonRebindable: true,
      },
    },
  ]),
  ...inputRichTextCommands.map((command) => {
    const { name, keybindings: bindings } = command;
    const keybindings = bindings.map((binding) => ({
      binding,
    }));

    return createCommand({
      group: 'richTextEditor',
      name,
      label: 'label' in command ? command.label : camelToSentenceCase(name),
      isExternal: true,
      keyboard: { keybindings },
    });
  }),
];
